import { Box, Grid, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import useAPI from "../../../../api/hooks/useAPI";
import useIsIntegrated from "../../../../api/hooks/useIsIntegrated";
import { integrationKeys } from "../../../../api/queryKeys";
import LoadingIcon from "../../../../components/LoadingIcon";
import { ScreenshotAccordion } from "../../../../components/ScreenshotAccordion";
import { Toast } from "../../../../components/Toast";
import PrimaryButton from "../../../../components/buttons/PrimaryButton";
import SecondaryButton from "../../../../components/buttons/SecondaryButton";
import useExpandedList from "../../../../components/hooks/ExpandedList";
import { logos } from "../../environment/icons/iconExport";
import Guideline from "../components/Guideline";
import GuidelineStep from "../components/GuidelineStep";
import GuidelineStepByStep from "../components/GuidelineStepByStep";
import IntegrationGuideAlert from "../components/IntegrationGuideAlert";
import CloudflareConnectForm from "./CloudflareConnectForm";
import cloudflare_step_1 from "./images/cloudflare_step_1.png";
import cloudflare_step_2 from "./images/cloudflare_step_2.png";
import cloudflare_step_3 from "./images/cloudflare_step_3.png";
import cloudflare_step_4 from "./images/cloudflare_step_4.png";
import cloudflare_step_5 from "./images/cloudflare_step_5.png";

const CloudflareIntegration = () => {
  const cloudflareLoginURL = "https://dash.cloudflare.com/login";
  const cloudflareCreateTokenURL =
    "https://dash.cloudflare.com/profile/api-tokens";

  const { isIntegrated, isIntegratedLoading } = useIsIntegrated("cloudflare");
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const [guideMode, setGuideMode] = useState(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (searchParams.has("viewonly")) {
      setGuideMode(true);
    }
  }, [searchParams]);

  const { addToExpandedList, handleExpandAll, handleCollapseAll } =
    useExpandedList();
  const { registerCloudflareIntegration } = useAPI();

  const onSubmit = (values) => {
    setLoading(true);
    registerCloudflareIntegration(values.api_token)
      .then((response) => {
        queryClient.invalidateQueries({
          queryKey: integrationKeys.all,
        });
        Toast.success("Cloudflare integration successful");
      })
      .catch((error) => {
        if (error.response) {
          Toast.error(`Something went wrong: ${error.response.data.detail}`);
        } else {
          Toast.error("An error occurred while processing the request.");
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  if (loading || isIntegratedLoading) {
    return <LoadingIcon />;
  }

  return (
    <Box>
      <Guideline
        title={
          !isIntegrated
            ? "Once integrated, the Breeze platform will automatically read the configurations from Cloudflare and prioritize the suggested remediations."
            : ""
        }
        image={logos.CloudflareLogo}
        name={"Cloudflare"}
        content={
          loading || isIntegratedLoading ? (
            <LoadingIcon size={25} />
          ) : isIntegrated && !guideMode ? (
            <Box>
              <Typography variant={"h4"}>Cloudflare is integrated!</Typography>
            </Box>
          ) : (
            <div>
              {/* BUTTONS TO CONTROL EXPANSION */}
              <Box mb="20px">
                <Grid container spacing={2} direction={"row"}>
                  {guideMode && <IntegrationGuideAlert />}

                  <Grid item>
                    <SecondaryButton onClick={handleExpandAll}>
                      Expand All Screenshots
                    </SecondaryButton>
                  </Grid>
                  <Grid item>
                    <SecondaryButton onClick={handleCollapseAll}>
                      Collapse All Screenshots
                    </SecondaryButton>
                  </Grid>
                </Grid>
              </Box>

              {/* SETUP INSTRUCTIONS */}
              <GuidelineStepByStep>
                <GuidelineStep title="Login to Cloudflare">
                  <Typography variant={"body2"}>
                    Login to your Cloudflare account
                  </Typography>
                  <Box style={{ display: "flex" }}>
                    <PrimaryButton
                      onClick={() => {
                        window.open(cloudflareLoginURL, "_blank");
                      }}
                    >
                      Login to Cloudflare Account
                    </PrimaryButton>
                  </Box>
                </GuidelineStep>
                <GuidelineStep title="Create a new API key">
                  <Typography variant={"body2"}>
                    Go to the API Tokens page and create a new API key
                  </Typography>
                  <Box style={{ display: "flex" }}>
                    <PrimaryButton
                      onClick={() => {
                        window.open(cloudflareCreateTokenURL, "_blank");
                      }}
                    >
                      API Tokens page
                    </PrimaryButton>
                  </Box>
                  <Typography variant={"body2"}>Create a new token</Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: cloudflare_step_1,
                        altext: "Cloudflare API Tokens Page",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant={"body2"}>
                    Select the <strong>{"Read all resources"}</strong> template
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: cloudflare_step_2,
                        altext: "Cloudflare API Tokens Page",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant={"body2"}>Continue to summary</Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: cloudflare_step_3,
                        altext: "Cloudflare API Tokens Page",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant={"body2"}>
                    Select <strong>{"Create Token"}</strong>
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: cloudflare_step_4,
                        altext: "Cloudflare API Tokens Page",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant={"body2"}>
                    Make sure to copy the API token
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: cloudflare_step_5,
                        altext: "Cloudflare API Tokens Page",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                </GuidelineStep>
                <GuidelineStep title="Paste the API token here">
                  <Box width={700}>
                    <CloudflareConnectForm
                      onSubmit={onSubmit}
                      guideMode={guideMode}
                    />
                  </Box>
                </GuidelineStep>
              </GuidelineStepByStep>
            </div>
          )
        }
      />
    </Box>
  );
};

export default CloudflareIntegration;
